import { tr } from '@repo/locale';
import { type TestId } from '@repo/test-id';
import { addDays, addMonths, startOfDay } from '@repo/date-time';
import { useCallback, useMemo, useState } from 'react';
import { ParkTimeContractType, type DateRange, type ParkTimeContract } from '@repo/domain';
import { useUnavailabilityDays } from '@repo/domain-service';
import { toast } from '@repo/toast';
import { DateRangePicker, type DateRangePickerValue, type DayFlag, Button, SubtitleSeparator } from '@repo/ui';
import { resolveUnavailabilityFlaggedDates } from '@repo/entities';

import TimeContractToggle from '@shared/components/timeContract/TimeContractToggle';
import TimeContractTimeDisplay from '@shared/components/timeContract/TimeContractTimeDisplay';
import TimeContractDurationInput from '@shared/components/timeContract/TimeContractDurationInput';
import TimeContractEndTimeDisplay from '@shared/components/timeContract/TimeContractEndTimeDisplay';
import TimeContractSlider from '@shared/components/timeContract/TimeContractSlider';
import {
  getToday,
  resolveParkingTime,
  calculateSliderValueFromContract,
  calculateDateRangeFromContract,
  createTimeContract,
} from '@shared/utils/timeContractCalculations';
import TimeContractPriceDisplay from '@shared/components/timeContract/TimeContractPriceDisplay';
import { MAX_RESERVATION_DURATION_MONTHS } from '@shared/constants/reservation';

interface Props {
  contract: ParkTimeContract;
  onChange: (contract: ParkTimeContract) => void;
  testId: TestId;
  withPriceFor?: string;
  spotId?: string;
}

export default function TimeContractPicker({ contract, onChange, testId, withPriceFor, spotId }: Props) {
  const startAt = useMemo<Date>(
    () => (contract.startAtTimestamp !== undefined ? new Date(contract.startAtTimestamp) : new Date()),
    [contract.startAtTimestamp]
  );
  const minDate = useMemo<Date>(() => startOfDay(startAt), [startAt]);
  const initialSliderValue: number = calculateSliderValueFromContract(contract);
  const initialDateRange: DateRange = calculateDateRangeFromContract(contract, startAt);

  const [timeContractType, setTimeContractType] = useState<ParkTimeContractType>(contract.type);
  const [sliderValue, setSliderValue] = useState<number>(initialSliderValue);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>(initialDateRange);
  const { parkingEndAt, duration } = useMemo(
    () => resolveParkingTime(timeContractType, selectedDateRange[1], sliderValue, startAt),
    [timeContractType, selectedDateRange, sliderValue, startAt]
  );
  const today = getToday();
  const maxReservationDate = addMonths(today, MAX_RESERVATION_DURATION_MONTHS);

  const { unavailabilityDays } = useUnavailabilityDays(
    withPriceFor ? { parkingId: withPriceFor, fromDate: today, toDate: maxReservationDate, type: 'full' } : undefined
  );
  const flaggedDates = useMemo<DayFlag[]>(
    () => resolveUnavailabilityFlaggedDates(unavailabilityDays, 'alert'),
    [unavailabilityDays]
  );

  const [buttonDisabledDuePriceCheck, setButtonDisabledDuePriceCheck] = useState<boolean>(false);
  const [isAvailable, setIsAvailable] = useState<boolean>(false);
  const disableReservation = !!(withPriceFor && !isAvailable);

  const handleHoursDaysSwitchMode = setTimeContractType;
  const handleSliderChange = (sliderValue: number) => {
    if (withPriceFor) {
      setButtonDisabledDuePriceCheck(true);
      setIsAvailable(true);
    }
    setSliderValue(sliderValue);
  };

  const handleDateRangeChange = (newRange: DateRangePickerValue): void => {
    if (withPriceFor) {
      setButtonDisabledDuePriceCheck(true);
      setIsAvailable(true);
    }
    setSelectedDateRange([minDate, newRange[1] ?? newRange[0] ?? addDays(minDate, 1)]);
  };

  const handlePriceLoaded = useCallback(() => {
    setButtonDisabledDuePriceCheck(false);
  }, []);
  const handleAvailability = setIsAvailable;

  const handleChange = () => {
    if (disableReservation) {
      toast.danger(tr('reservations:duration_page.parking_unavailable'), testId.append('parkingUnavailable'));
    } else {
      const newContract = createTimeContract(timeContractType, selectedDateRange[1], sliderValue);
      onChange(newContract);
    }
  };

  return (
    <>
      <div className="mt-5 flex flex-row items-center justify-start gap-2">
        <TimeContractToggle
          type={timeContractType}
          onSwitch={handleHoursDaysSwitchMode}
          testId={testId.append('hoursDaysSwitch')}
        />
      </div>

      <div className="mt-4 flex flex-row gap-4">
        <TimeContractTimeDisplay
          parkingStartAt={contract.startAtTimestamp !== undefined ? startAt : undefined}
          parkingEndAt={parkingEndAt}
          parkDuration={duration}
          testId={testId.append('parkDuration')}
        />
        {withPriceFor && (
          <TimeContractPriceDisplay
            parkingId={withPriceFor}
            parkDuration={duration}
            onPriceLoaded={handlePriceLoaded}
            onAvailability={handleAvailability}
            spotId={spotId}
            testId={testId.append('parkPrice')}
          />
        )}
      </div>

      {timeContractType === ParkTimeContractType.FixedDuration && (
        <>
          <SubtitleSeparator primary className="my-6">
            {tr('parking_search:park_duration.how_many_hours')}
          </SubtitleSeparator>
          <TimeContractDurationInput
            parkDuration={sliderValue}
            onChange={handleSliderChange}
            testId={testId.append('timeDurationDisplay')}
          />
        </>
      )}

      {timeContractType === ParkTimeContractType.FixedEnd && (
        <>
          <SubtitleSeparator primary className="my-6">
            {tr('parking_search:park_duration.park_until_day')}
          </SubtitleSeparator>
          <DateRangePicker
            value={selectedDateRange}
            onChange={handleDateRangeChange}
            disablePast
            minDate={minDate}
            maxDate={maxReservationDate}
            flaggedDates={flaggedDates}
            testId={testId.append('dateRangePicker')}
          />
          <SubtitleSeparator primary className="my-6">
            {tr('parking_search:park_duration.until_time')}
          </SubtitleSeparator>
          <TimeContractEndTimeDisplay parkDuration={sliderValue} testId={testId.append('endTime')} />
        </>
      )}

      <div className="mt-5 mb-6 h-13.5 w-full rounded-lg">
        <TimeContractSlider
          onSliderChange={handleSliderChange}
          value={sliderValue}
          timeContract={timeContractType}
          date={selectedDateRange[1]}
          startAt={startAt}
        />
      </div>

      <Button
        testId={testId.append('confirm')}
        onClick={handleChange}
        color="brand"
        size="lg"
        className="mb-5 w-full"
        disabled={buttonDisabledDuePriceCheck || disableReservation}
      >
        {disableReservation
          ? tr('parking_search:park_duration.no_availability')
          : tr('parking_search:park_duration.confirm_selection')}
      </Button>
    </>
  );
}
